import type { WorkshopPlan } from '@features/workshop-plan/types';
import type { PlanFocus } from '@features/workshop-plan/utils/plan-cell';

import { cn } from '@shared/utils/cn';

import { PlanCellTile } from '@features/workshop-plan/components/plan-cell-tile';
import { PlanShelfGrid } from '@features/workshop-plan/components/plan-shelf-grid';
import {
  getPlanTitle,
  getPlanCells,
  matchesFocus,
  getAisleTitle,
  getCellStatus,
  getZoneWeight,
  getShelfWeight,
  getAisleWeight,
  getFilledShelves,
} from '@features/workshop-plan/utils/plan-cell';

/** Below this width per cell of the fullest band the zone scrolls sideways instead of squeezing the cells. */
const MIN_CELL_WIDTH = '3rem';

type Props = {
  plan: WorkshopPlan;
  focus: PlanFocus;
};

/** A zone: its aisles side by side, separated by a dashed line, or its large floor cells. */
export const PlanZone = ({ plan, focus }: Props) => {
  const aisles = plan.aisles.filter((aisle) => getAisleWeight(aisle) > 0);
  const cells = getPlanCells([plan]);
  const occupied = cells.filter((cell) => getCellStatus(cell).occupied).length;
  const matching = cells.filter((cell) => matchesFocus(cell, focus)).length;
  const filtering = focus.tone !== null || focus.query.trim() !== '';

  return (
    <section
      aria-labelledby={`plan-zone-${plan.id}`}
      className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5"
    >
      <div className="flex items-baseline gap-4">
        <h2 className="text-lg font-semibold" id={`plan-zone-${plan.id}`}>
          {getPlanTitle(plan)}
        </h2>
        <span aria-live="polite" className="ml-auto text-sm text-muted-foreground tabular-nums">
          {filtering
            ? `${matching} emplacement${matching > 1 ? 's' : ''} sur ${cells.length}`
            : `${occupied} / ${cells.length} occupés`}
        </span>
      </div>

      {cells.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun emplacement dans cette zone.</p>
      ) : plan.floor ? (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(11rem, 1fr))' }}>
          {plan.floorCells.map((cell) => (
            <div className="grid [&>*]:min-h-24 [&>*]:border-green-500/60" key={cell.id}>
              <PlanCellTile cell={cell} dashed dimmed={!matchesFocus(cell, focus)} minHeight={96} />
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto pb-2">
          {/* Les allées se partagent la largeur de l'écran au prorata de leur poids ; sous le minimum, la zone défile. */}
          <div className="flex items-start" style={{ minWidth: `calc(${getZoneWeight(plan)} * ${MIN_CELL_WIDTH})` }}>
            {aisles.map((aisle, index) => (
              <div
                className={cn(
                  'flex min-w-0 flex-col gap-2',
                  index === 0 ? 'pl-0' : 'border-l-2 border-dashed border-border pl-4',
                  index === aisles.length - 1 ? 'pr-0' : 'pr-4'
                )}
                key={aisle.id}
                style={{ flex: `${getAisleWeight(aisle)} 1 0` }}
              >
                <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {getAisleTitle(aisle)}
                </span>
                <div className="flex items-start gap-3">
                  {getFilledShelves(aisle).map((shelf) => (
                    <div className="min-w-0" key={shelf.id} style={{ flex: `${getShelfWeight(shelf)} 1 0` }}>
                      <PlanShelfGrid focus={focus} shelf={shelf} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
